import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { useCompanyNameOptions } from '@/api/activity';
import {
  AddOptionRow,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxStatus,
  ComboboxValue,
  DebouncedCombobox,
  InfiniteComboboxList,
  useAddableCombobox,
  type ComboboxProps,
} from '@/components/Form/pickers';

const PAGE_SIZE = 50;

const CompanyNameMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  className,
  disabled = false,
  size = 'large',
  placeholder = t`Select Company Names...`,
  'aria-invalid': ariaInvalid,
  ...rest
}: Omit<
  ComboboxProps<string, true>,
  'items' | 'multiple' | 'value' | 'onValueChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value?: string[];
  isLoading?: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const { companyOptions, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useCompanyNameOptions({
      search,
      pageSize: PAGE_SIZE,
    });

  const items = useMemo(
    () =>
      Array.from(
        new Set(
          (companyOptions ?? [])
            .map((option) => option.name ?? '')
            .filter((name) => name.length > 0),
        ),
      ),
    [companyOptions],
  );

  const { isAddOption, isAddOptionSelected, itemsWithAddOption } = useAddableCombobox({
    inputSearch,
    value,
    items,
  });

  return (
    <DebouncedCombobox
      {...rest}
      data-testid="company-name-multiselect"
      multiple
      filteredItems={itemsWithAddOption}
      isLoading={isLoadingProp || isLoading || isFetchingNextPage}
      disabled={disabled}
      size={size}
      value={value ?? null}
      onInputValueChange={(nextSearch) => setInputSearch(nextSearch)}
      onSearch={setSearch}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item}>{item}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No companies were found...</Trans>
        </ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
        >
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {isAddOption(item) ? (
                <AddOptionRow label={item} isSelected={isAddOptionSelected} />
              ) : (
                item
              )}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default CompanyNameMultiSelect;
